import React, { useState, DragEvent, useEffect } from 'react';
import { Search, Plus, X, Settings, Eye, GripVertical, Loader2, AlertTriangle, RefreshCw, Check } from 'lucide-react';
import { Model } from '../../types';
import ModelSchemaPreview from './ModelSchemaPreview';
import Tooltip from '../common/Tooltip';

interface ModelAttachmentSystemProps {
  models: Model[];
  attachedModelIds: number[];
  onModelAttach: (modelId: number) => void;
  onModelDetach: (modelId: number) => void;
  onModelReorder: (startIndex: number, endIndex: number) => void;
  onModelPreview: (model: Model) => void;
  onModelConfigure?: (model: Model) => void;
  maxAttachments?: number;
  allowReordering?: boolean;
  isLoadingModels?: boolean;
  onRefreshModels?: () => Promise<void>;
  showAvailableModels?: boolean;
  onToggleAvailableModels?: (show: boolean) => void;
}

const ModelAttachmentSystem: React.FC<ModelAttachmentSystemProps> = ({
  models,
  attachedModelIds,
  onModelAttach,
  onModelDetach,
  onModelReorder,
  onModelPreview,
  onModelConfigure,
  maxAttachments,
  allowReordering = true,
  isLoadingModels = false,
  onRefreshModels,
  showAvailableModels,
  onToggleAvailableModels
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [refreshingModels, setRefreshingModels] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null);
  const [previewingModelId, setPreviewingModelId] = useState<number | null>(null);

  // Deduplicate models by model_id to prevent React key conflicts
  const deduplicatedModels = models.filter((model, index, self) => 
    index === self.findIndex(m => m.model_id === model.model_id)
  );

  const attachedModels = deduplicatedModels.filter(model => attachedModelIds.includes(model.model_id));
  const availableModels = deduplicatedModels.filter(model => !attachedModelIds.includes(model.model_id));
  
  const filteredAvailableModels = availableModels
    .filter(model =>
      model.class_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      model.description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => a.class_name.localeCompare(b.class_name));

  const canAttachMore = !maxAttachments || attachedModelIds.length < maxAttachments;
  const missingModelIds = attachedModelIds.filter(id => !deduplicatedModels.some(m => m.model_id === id));

  // Set selected model to the attached model by default
  useEffect(() => {
    if (attachedModelIds.length > 0 && !selectedModelId) {
      setSelectedModelId(attachedModelIds[0]);
    } else if (attachedModelIds.length === 0 && selectedModelId) {
      setSelectedModelId(null);
    }
  }, [attachedModelIds, selectedModelId]);

  const handleRefreshModels = async () => {
    if (!onRefreshModels) return;
    setRefreshingModels(true);
    try {
      await onRefreshModels();
    } finally {
      setRefreshingModels(false);
    }
  };

  const handleModelSelect = (modelId: number) => {
    setSelectedModelId(modelId);
    
    // If this model is not currently attached, attach it
    if (!attachedModelIds.includes(modelId)) {
      // If at max attachments, detach current model first
      if (!canAttachMore && attachedModelIds.length > 0) {
        onModelDetach(attachedModelIds[0]);
      }
      onModelAttach(modelId);
    }
    
    // Close the dropdown after selection
    onToggleAvailableModels?.(false);
  };

  const handleModelDetach = (modelId: number) => {
    onModelDetach(modelId);
    if (selectedModelId === modelId) {
      setSelectedModelId(null);
    }
  };

  const handlePreviewToggle = (modelId: number) => {
    setPreviewingModelId(previewingModelId === modelId ? null : modelId);
  };

  const selectedModel = deduplicatedModels.find(m => m.model_id === selectedModelId);
  const previewingModel = deduplicatedModels.find(m => m.model_id === previewingModelId);

  return (
    <div className="space-y-4">
      {/* Model Selection Section */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">Output Schema</h3>
          <div className="flex items-center space-x-2">
            {missingModelIds.length > 0 && onRefreshModels && (
              <Tooltip content="Refresh schemas to resolve missing schemas">
              <button
                type="button"
                onClick={handleRefreshModels}
                disabled={refreshingModels || isLoadingModels}
                className="text-sm text-orange-600 hover:text-orange-700 flex items-center disabled:opacity-50"
              >
                {refreshingModels ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-1" />
                )}
                Refresh
              </button>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Available Models Dropdown */}
        {showAvailableModels && (
          <div className="mb-4 border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Available Schemas</h4>
              <button
                onClick={() => onToggleAvailableModels?.(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {!canAttachMore && (
              <div className="mb-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  <span className="font-medium">Note:</span> You have reached the maximum number of schemas.
                  Selecting a schema will replace the current one.
                </p>
              </div>
            )}

            <div className="mb-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search schemas..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredAvailableModels.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  {searchQuery ? 'No schemas match your search' : 'No available schemas'}
                </p>
              ) : (
                filteredAvailableModels.map((model) => (
                  <ModelOptionCard
                    key={model.model_id}
                    model={model}
                    isSelected={selectedModelId === model.model_id}
                    onSelect={() => handleModelSelect(model.model_id)}
                    onPreview={() => onModelPreview(model)}
                    isPreviewing={previewingModelId === model.model_id}
                    onPreviewToggle={() => handlePreviewToggle(model.model_id)}
                  />
                ))
              )}
            </div>
          </div>
        )}

        {/* Selected Model Display */}
        {selectedModel && (
          <div className="border border-primary-200 dark:border-primary-700 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <div>
                  <h4 className="font-medium text-blue-900 dark:text-blue-200">{selectedModel.class_name}</h4>
                  {selectedModel.description && (
                    <p className="text-sm text-blue-700 dark:text-blue-400">{selectedModel.description}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Tooltip content="Preview schema">
                  <button
                    type="button"
                    onClick={() => onModelPreview(selectedModel)}
                    className="p-1 text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </Tooltip>
                {onModelConfigure && (
                  <Tooltip content="Configure parameters">
                    <button
                      type="button"
                      onClick={() => onModelConfigure(selectedModel)}
                      className="p-1 text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                  </Tooltip>
                )}
                <Tooltip content="Remove schema">
                  <button
                    type="button"
                    onClick={() => handleModelDetach(selectedModel.model_id)}
                    className="p-1 text-red-600 hover:text-red-700 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
        )}

        {/* No Model Selected */}
        {!selectedModel && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
            <div className="text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">No schema selected</p>
              <p className="text-xs text-gray-400 mt-1">Use the schema selection above to choose an output schema</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Model Option Card Component
interface ModelOptionCardProps {
  model: Model;
  isSelected: boolean;
  onSelect: () => void;
  onPreview: () => void;
  isPreviewing: boolean;
  onPreviewToggle: () => void;
}

const ModelOptionCard: React.FC<ModelOptionCardProps> = ({
  model,
  isSelected,
  onSelect,
  onPreview,
  isPreviewing,
  onPreviewToggle
}) => {
  return (
    <div className="space-y-2">
      <div
        className={`border rounded-lg p-3 cursor-pointer transition-all ${
          isSelected 
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-cap-dark hover:border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900'
        }`}
        onClick={onSelect}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="font-medium text-gray-900 dark:text-warm-white">{model.class_name}</div>
            {model.description && (
              <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{model.description}</div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Tooltip content={isPreviewing ? "Hide schema preview" : "Preview schema"}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onPreviewToggle();
                }}
                className={`p-1 transition-colors ${
                  isPreviewing
                    ? 'text-primary-600 hover:text-primary-700'
                    : 'text-gray-400 hover:text-primary-600'
                }`}
              >
                <Eye className="w-4 h-4" />
              </button>
            </Tooltip>
            {isSelected && (
              <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Inline Schema Preview */}
      {isPreviewing && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center justify-between mb-2">
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Schema Preview</h5>
            <button
              type="button"
              onClick={onPreviewToggle}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <ModelSchemaPreview model={model} />
        </div>
      )}
    </div>
  );
};

export default ModelAttachmentSystem;